import type { SegmentData, GroupBlock } from "../types";

/** Find a GROUP block by ID across all segments. */
export function findGroupBlock(
  segments: SegmentData[],
  groupId: string | null,
): GroupBlock | undefined {
  if (!groupId) return undefined;
  for (const seg of segments) {
    for (const block of seg.items) {
      if (block.blockType === "GROUP" && block._id === groupId) {
        return block as GroupBlock;
      }
    }
  }
  return undefined;
}

/** Get the set of stop IDs within a group (excludes tweens). */
export function getGroupStopIds(group: GroupBlock): Set<string> {
  return new Set(
    group.items.filter(b => b.blockType === "STOP").map(b => b._id),
  );
}

/** Build a map of groupId → groupType for all groups across segments. */
export function buildGroupTypeById(
  segments: SegmentData[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const seg of segments) {
    for (const block of seg.items) {
      if (block.blockType === "GROUP") {
        const group = block as GroupBlock;
        map.set(group._id, group.groupType ?? "SEQUENTIAL");
      }
    }
  }
  return map;
}

/**
 * Get the stop IDs of the active group (any group type).
 * Returns an empty set when not inside a group.
 */
export function getActiveGroupStopIds(
  segments: SegmentData[],
  activeStopGroupId: string | null,
): Set<string> {
  if (!activeStopGroupId) return new Set();
  const group = findGroupBlock(segments, activeStopGroupId);
  if (!group) return new Set();
  return getGroupStopIds(group);
}

/** Collect all stop IDs from every OPTIONS group across all segments. */
export function collectAllOptionsGroupStopIds(
  segments: SegmentData[],
): Set<string> {
  const ids = new Set<string>();
  for (const seg of segments) {
    for (const block of seg.items) {
      if (block.blockType === "GROUP") {
        const group = block as GroupBlock;
        if (group.groupType === "OPTIONS") {
          for (const child of group.items) {
            if (child.blockType === "STOP") ids.add(child._id);
          }
        }
      }
    }
  }
  return ids;
}
